'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useState, useEffect, useCallback, useMemo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import FeaturedProductsPopup from '@/app/_components/FeaturedProductsPopup';
import '@/app/_styles/slideshow.css';

/**
 * Shuffle array using Fisher-Yates algorithm
 */
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
const Slideshow = t0 => {
  const $ = _c(66);
  const {
    slides,
    ratio: t1,
    className: t2,
    leftImageBlend: t3,
    rightImageBlend: t4,
    maxSlides: t5,
    onSlideChange
  } = t0;
  const ratio = t1 === undefined ? "thirds" : t1;
  const className = t2 === undefined ? "" : t2;
  const leftImageBlend = t3 === undefined ? false : t3;
  const rightImageBlend = t4 === undefined ? false : t4;
  const maxSlides = t5 === undefined ? 5 : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const [randomizedSlides, setRandomizedSlides] = useState(t6);
  let t7;
  let t8;
  if ($[1] !== maxSlides || $[2] !== slides) {
    t7 = () => {
      if (slides?.length > 0) {
        const shuffled = shuffleArray(slides);
        const selected = shuffled.slice(0, maxSlides);
        setRandomizedSlides(selected);
      }
    };
    t8 = [slides, maxSlides];
    $[1] = maxSlides;
    $[2] = slides;
    $[3] = t7;
    $[4] = t8;
  } else {
    t7 = $[3];
    t8 = $[4];
  }
  useEffect(t7, t8);
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      loop: true,
      duration: 425
    };
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const [emblaOptions] = useState(t9);
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [Fade(), Autoplay({
      delay: 30000,
      stopOnInteraction: false,
      stopOnFocusIn: false
    })];
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const plugins = t10;
  const [viewportRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);
  let t11;
  if ($[7] !== emblaApi || $[8] !== onSlideChange || $[9] !== randomizedSlides) {
    t11 = () => {
      if (!emblaApi || !onSlideChange) {
        return;
      }
      const index = emblaApi.selectedScrollSnap();
      if (randomizedSlides[index]) {
        onSlideChange(randomizedSlides[index]);
      }
    };
    $[7] = emblaApi;
    $[8] = onSlideChange;
    $[9] = randomizedSlides;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const onSelect = t11;
  let t12;
  let t13;
  if ($[11] !== emblaApi || $[12] !== onSelect || $[13] !== randomizedSlides.length) {
    t12 = () => {
      if (!emblaApi || randomizedSlides.length === 0) {
        return;
      }
      emblaApi.on("select", onSelect);
      onSelect();
      return () => emblaApi.off("select", onSelect);
    };
    t13 = [emblaApi, onSelect, randomizedSlides.length];
    $[11] = emblaApi;
    $[12] = onSelect;
    $[13] = randomizedSlides.length;
    $[14] = t12;
    $[15] = t13;
  } else {
    t12 = $[14];
    t13 = $[15];
  }
  useEffect(t12, t13);
  let t14;
  if ($[16] !== onSlideChange || $[17] !== randomizedSlides[0] || $[18] !== randomizedSlides.length) {
    t14 = () => {
      if (randomizedSlides.length === 1 && onSlideChange) {
        onSlideChange(randomizedSlides[0]);
      }
    };
    $[16] = onSlideChange;
    $[17] = randomizedSlides[0];
    $[18] = randomizedSlides.length;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== onSlideChange || $[21] !== randomizedSlides) {
    t15 = [randomizedSlides, onSlideChange];
    $[20] = onSlideChange;
    $[21] = randomizedSlides;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  useEffect(t14, t15);
  const leftWidth = ratio === "fifths" ? "w-3/5" : ratio === "fifths-reversed" ? "w-2/5" : "w-2/3";
  const rightWidth = ratio === "fifths" ? "w-2/5" : ratio === "fifths-reversed" ? "w-3/5" : "w-1/3";
  const leftSizes = ratio === "fifths" ? "60vw" : ratio === "fifths-reversed" ? "40vw" : "67vw";
  const rightSizes = ratio === "fifths" ? "40vw" : ratio === "fifths-reversed" ? "60vw" : "33vw";
  if (randomizedSlides.length === 1) {
    const slide = randomizedSlides[0];
    const t16 = `relative ${className}`;
    const t17 = `${leftWidth} ${leftImageBlend ? "mix-blend-overlay" : ""}`;
    let t18;
    if ($[23] !== leftSizes || $[24] !== slide.leftImage.alt || $[25] !== slide.leftImage.height || $[26] !== slide.leftImage.src || $[27] !== slide.leftImage.width) {
      t18 = <Image src={slide.leftImage.src} alt={slide.leftImage.alt} width={slide.leftImage.width} height={slide.leftImage.height} className="w-full h-auto" priority={true} quality={90} sizes={leftSizes} />;
      $[23] = leftSizes;
      $[24] = slide.leftImage.alt;
      $[25] = slide.leftImage.height;
      $[26] = slide.leftImage.src;
      $[27] = slide.leftImage.width;
      $[28] = t18;
    } else {
      t18 = $[28];
    }
    let t19;
    if ($[29] !== t17 || $[30] !== t18) {
      t19 = <div className={t17}>{t18}</div>;
      $[29] = t17;
      $[30] = t18;
      $[31] = t19;
    } else {
      t19 = $[31];
    }
    const t20 = `${rightWidth} ${rightImageBlend ? "mix-blend-overlay" : ""}`;
    let t21;
    if ($[32] !== rightSizes || $[33] !== slide.rightImage.alt || $[34] !== slide.rightImage.height || $[35] !== slide.rightImage.src || $[36] !== slide.rightImage.width) {
      t21 = <Image src={slide.rightImage.src} alt={slide.rightImage.alt} width={slide.rightImage.width} height={slide.rightImage.height} className="w-full h-auto" priority={true} quality={90} sizes={rightSizes} />;
      $[32] = rightSizes;
      $[33] = slide.rightImage.alt;
      $[34] = slide.rightImage.height;
      $[35] = slide.rightImage.src;
      $[36] = slide.rightImage.width;
      $[37] = t21;
    } else {
      t21 = $[37];
    }
    let t22;
    if ($[38] !== t20 || $[39] !== t21) {
      t22 = <div className={t20}>{t21}</div>;
      $[38] = t20;
      $[39] = t21;
      $[40] = t22;
    } else {
      t22 = $[40];
    }
    let t23;
    if ($[41] !== t19 || $[42] !== t22) {
      t23 = <div className="flex w-full">{t19}{t22}</div>;
      $[41] = t19;
      $[42] = t22;
      $[43] = t23;
    } else {
      t23 = $[43];
    }
    let t24;
    if ($[44] !== slide.featured_products) {
      t24 = <FeaturedProductsPopup products={slide.featured_products} position="bottom-left" />;
      $[44] = slide.featured_products;
      $[45] = t24;
    } else {
      t24 = $[45];
    }
    let t25;
    if ($[46] !== t16 || $[47] !== t23 || $[48] !== t24) {
      t25 = <div className={t16}>{t23}{t24}</div>;
      $[46] = t16;
      $[47] = t23;
      $[48] = t24;
      $[49] = t25;
    } else {
      t25 = $[49];
    }
    return t25;
  }
  const t16 = `embla transition-slideshow ${className}`;
  let t17;
  if ($[50] !== leftImageBlend || $[51] !== leftSizes || $[52] !== leftWidth || $[53] !== randomizedSlides || $[54] !== rightImageBlend || $[55] !== rightSizes || $[56] !== rightWidth) {
    t17 = randomizedSlides.length > 0 && randomizedSlides.map(slide_0 => <div key={slide_0.id} className="embla__slide transition-slideshow-slide relative"><div className="flex w-full"><div className={`${leftWidth} ${leftImageBlend ? "mix-blend-overlay" : ""}`}><Image src={slide_0.leftImage.src} alt={slide_0.leftImage.alt} width={slide_0.leftImage.width} height={slide_0.leftImage.height} className="w-full h-auto" priority={true} quality={90} sizes={leftSizes} /></div><div className={`${rightWidth} ${rightImageBlend ? "mix-blend-overlay" : ""}`}><Image src={slide_0.rightImage.src} alt={slide_0.rightImage.alt} width={slide_0.rightImage.width} height={slide_0.rightImage.height} className="w-full h-auto" priority={true} quality={90} sizes={rightSizes} /></div></div><FeaturedProductsPopup products={slide_0.featured_products} position="bottom-left" /></div>);
    $[50] = leftImageBlend;
    $[51] = leftSizes;
    $[52] = leftWidth;
    $[53] = randomizedSlides;
    $[54] = rightImageBlend;
    $[55] = rightSizes;
    $[56] = rightWidth;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== t17) {
    t18 = <div className="embla__container transition-slideshow-container">{t17}</div>;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== t18 || $[61] !== viewportRef) {
    t19 = <div className="embla__viewport transition-slideshow-viewport" ref={viewportRef}>{t18}</div>;
    $[60] = t18;
    $[61] = viewportRef;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== t16 || $[64] !== t19) {
    t20 = <div className={t16}>{t19}</div>;
    $[63] = t16;
    $[64] = t19;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  return t20;
};
export default Slideshow;